import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import {
  useGetAccountQuery,
  useGetProjectByIdQuery,
  useListClientsForAccountQuery,
  useListSalesExecutivesForAccountQuery,
  useListPresalesEngineersForAccountQuery,
  useListPaymentTermsForAccountQuery,
  useListRateTablesForAccountQuery,
  useListBusinessUnitsForAccountQuery,
  useListProjectLocationsQuery,
} from "@generated";

interface OptionItem {
  value: string;
  label: string;
}

const useProjectOverviewData = (projectId: string) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);

  const {
    data: account,
    error: accountError,
    isLoading: accountLoading,
  } = useGetAccountQuery({ slug: accountSlug });

  const {
    data: project,
    error: projectError,
    isLoading: projectLoading,
    refetch: refetchProject,
  } = useGetProjectByIdQuery({
    slug: accountSlug,
    id: Number(projectId),
    include: [
      "account",
      "business-unit",
      "external-request",
      "project-locations",
      "payment-term",
      "client",
      "rate-table",
      "sales-executive",
      "presales-engineer",
      "crm-opportunity",
      "psa-project",
    ],
  });

  const {
    data: clientsData,
    error: clientsError,
    isLoading: clientsLoading,
  } = useListClientsForAccountQuery({
    slug: accountSlug,
    filter: { active: true },
    page: { size: 100 },
    include: ["rate-table"],
  });

  const {
    data: salesExecsData,
    error: salesExecsError,
    isLoading: salesExecsLoading,
  } = useListSalesExecutivesForAccountQuery({
    slug: accountSlug,
    page: { size: 500 },
  });

  const {
    data: presalesData,
    error: presalesError,
    isLoading: presalesLoading,
  } = useListPresalesEngineersForAccountQuery({
    slug: accountSlug,
    filter: { active: "true,false" as unknown as boolean },
    page: { size: 500 },
  });

  const {
    data: paymentTermsData,
    error: paymentTermsError,
    isLoading: paymentTermsLoading,
  } = useListPaymentTermsForAccountQuery({
    slug: accountSlug,
  });

  const {
    data: rateTablesData,
    error: rateTablesError,
    isLoading: rateTablesLoading,
  } = useListRateTablesForAccountQuery({
    slug: accountSlug,
    page: { size: 500 },
  });

  const {
    data: businessUnitsData,
    error: businessUnitsError,
    isLoading: businessUnitsLoading,
  } = useListBusinessUnitsForAccountQuery({
    slug: accountSlug,
  });

  const {
    data: locationsData,
    error: locationsError,
    isLoading: locationsLoading,
    refetch: refetchLocations,
  } = useListProjectLocationsQuery({
    slug: accountSlug,
    filter: { project: projectId },
  });

  // Transform account data
  const isCrm = account?.data?.attributes?.["crm?"] ?? false;
  const isPsa = account?.data?.attributes?.["psa?"] ?? false;
  const fieldLabels: Record<string, string> = account?.data?.attributes?.[
    "field-labels"
  ] ?? {
    presales_engineer: "Pre-Sales Engineer",
    sales_executive: "Sales Executive",
    business_unit: "Business Unit",
    location: "Service Location",
    client: "Client",
    crm_opportunity: "CRM Opportunity",
    request_approval: "Request Approval",
  };
  // The project relationship needs the numeric account-id, not the JSON:API resource id
  const accountId =
    String(
      account?.data?.attributes?.["account-id"] || account?.data?.id || ""
    ) || undefined;

  // Transform project data
  const projectData = project?.data;
  const includedData = project?.included || [];

  // Extract included relationships
  const includedClient = includedData.find((item) => item.type === "clients");
  const includedPaymentTerm = includedData.find(
    (item) => item.type === "payment-terms"
  );
  const includedBusinessUnit = includedData.find(
    (item) => item.type === "business-units"
  );
  const includedSalesExec = includedData.find(
    (item) => item.type === "sales-executives"
  );
  const includedPresales = includedData.find(
    (item) => item.type === "presales-engineers"
  );
  const includedCrm = includedData.find(
    (item) => item.type === "crm-opportunities"
  );
  const includedRateTable = includedData.find(
    (item) => item.type === "rate-tables"
  );
  const includedExternalRequest = includedData.find(
    (item) => item.type === "external-requests"
  );
  const includedPsa = includedData.find((item) => item.type === "psa-projects");

  // Transform clients for autocomplete
  const clients =
    clientsData?.data?.map((client) => ({
      id: client.id?.toString() || "",
      name: client.attributes?.name || "",
      msaDate: client.attributes?.["msa-date"] || null,
      rateTableId:
        client.relationships?.["rate-table"]?.data?.id?.toString() || null,
    })) || [];

  const clientNames = clients.map((c) => c.name);

  // Transform sales executives for dropdown
  const salesExecOptions: OptionItem[] =
    salesExecsData?.data?.map((se) => ({
      value: se.id?.toString() || "",
      label: se.attributes?.name || "",
    })) || [];

  // Transform presales engineers for dropdown
  // When building, show only active engineers; otherwise show all so
  // inactive/departed engineers still appear on past projects.
  const projectStatus = projectData?.attributes?.status;
  const filteredPresales =
    projectStatus === "building"
      ? presalesData?.data?.filter((pe) => (pe.attributes as any)?.active) || []
      : presalesData?.data || [];
  const presalesOptions: OptionItem[] = filteredPresales.map((pe) => ({
    value: pe.id?.toString() || "",
    label: pe.attributes?.name || "",
  }));

  // Transform payment terms for dropdown
  const paymentTermOptions: OptionItem[] =
    paymentTermsData?.data?.map((pt) => ({
      value: pt.id?.toString() || "",
      label: pt.attributes?.name || "",
    })) || [];

  // Transform rate tables (active only) for dropdown
  const activeRateTables =
    rateTablesData?.data?.filter((rt) => (rt.attributes as any)?.active) || [];

  // Include the project's current rate table even if inactive
  if (
    includedRateTable &&
    !activeRateTables.find((rt) => rt.id === includedRateTable.id)
  ) {
    activeRateTables.push(includedRateTable as any);
  }

  const rateTableOptions: OptionItem[] = activeRateTables.map((rt) => ({
    value: rt.id?.toString() || "",
    label: rt.attributes?.name || "",
  }));

  // Transform business units for dropdown
  const businessUnitOptions: OptionItem[] =
    businessUnitsData?.data?.map((bu) => ({
      value: bu.id?.toString() || "",
      label: bu.attributes?.name || "",
    })) || [];

  // Transform project locations
  const projectLocations = locationsData?.data || [];

  const isLoading =
    accountLoading ||
    projectLoading ||
    clientsLoading ||
    salesExecsLoading ||
    presalesLoading ||
    paymentTermsLoading ||
    rateTablesLoading ||
    businessUnitsLoading ||
    locationsLoading;

  const hasError =
    accountError ||
    projectError ||
    clientsError ||
    salesExecsError ||
    presalesError ||
    paymentTermsError ||
    rateTablesError ||
    businessUnitsError ||
    locationsError;

  return {
    // Raw data
    account,
    projectData,
    includedData,
    refetchProject,
    refetchLocations,

    // Account info
    accountSlug,
    accountId,
    isCrm,
    isPsa,
    fieldLabels,

    // Included relationships
    includedClient,
    includedPaymentTerm,
    includedBusinessUnit,
    includedSalesExec,
    includedPresales,
    includedCrm,
    includedRateTable,
    includedExternalRequest,
    includedPsa,

    // Transformed options
    clients,
    clientNames,
    salesExecOptions,
    presalesOptions,
    paymentTermOptions,
    rateTableOptions,
    businessUnitOptions,
    projectLocations,

    // Loading / error
    isLoading,
    hasError,
  };
};

export default useProjectOverviewData;
